// src/routes/index.tsx
import { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { QuantumPageLoader } from '@/components/QuantumLoadingStates';
import type { AppRoute } from './core/types';

// Import all domain loaders
import { loadCanadaRoutes } from './domains/canada';
import { loadUkRoutes } from './domains/uk';
import { loadGermanyRoutes } from './domains/germany';
import { loadAustraliaRoutes } from './domains/australia';
import { loadNzRoutes } from './domains/newzealand';
import { loadIrelandRoutes } from './domains/ireland';
import { loadAgencyRoutes } from './domains/agency';
import { loadClientRoutes } from './domains/client';
import { loadQuantumRoutes } from './domains/quantum';
import { loadGlobalRoutes } from './domains/global';
import { loadCoreRoutes } from './domains/core';
import { gateNonUkCountryRoutes } from './core/countryGate';

// Feature flag
const USE_MODULAR_ROUTES =
  import.meta.env.VITE_USE_MODULAR_ROUTES === 'true' ||
  localStorage.getItem('use_modular_routes') === 'true';

// Legacy fallback (placeholder – replace with your actual legacy router)
const LegacyRouter = () => (
  <div className="p-8 text-center">
    <h1>Legacy Router Active</h1>
    <p>Switch to modular router by setting VITE_USE_MODULAR_ROUTES=true</p>
  </div>
);

// Simple 404 component
const NotFoundPage = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-800">404</h1>
      <p className="text-gray-600 mt-2">Page not found</p>
    </div>
  </div>
);

/**
 * Loads all domain route arrays in parallel and merges them.
 */
async function loadAllRoutes(): Promise<AppRoute[]> {
  const [
    canada,
    uk,
    germany,
    australia,
    nz,
    ireland,
    agency,
    client,
    quantum,
    global,
    core,
  ] = await Promise.all([
    loadCanadaRoutes(),
    loadUkRoutes(),
    loadGermanyRoutes(),
    loadAustraliaRoutes(),
    loadNzRoutes(),
    loadIrelandRoutes(),
    loadAgencyRoutes(),
    loadClientRoutes(),
    loadQuantumRoutes(),
    loadGlobalRoutes(),
    loadCoreRoutes(),
  ]);

  const merged = [
    ...canada,
    ...uk,
    ...germany,
    ...australia,
    ...nz,
    ...ireland,
    ...agency,
    ...client,
    ...quantum,
    ...global,
    ...core,
  ];

  // Layer 2 of the UK Country Gate — applied once, here, to the fully
  // merged table every request is actually matched against. Country
  // routes are scattered across six domain files (and ~350 of them live
  // inside `core` alongside UK and country-agnostic routes), so this is
  // the one point that sees every path regardless of which file it came
  // from. Any direct URL into a blocked country's page — typed, bookmarked,
  // shared — redirects to /countries instead of rendering, unconditionally.
  return gateNonUkCountryRoutes(merged);
}

/**
 * Modular Router Component
 */
export function ModularRouter() {
  const [allRoutes, setAllRoutes] = useState<AppRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadAllRoutes()
      .then((routes) => {
        setAllRoutes(routes);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load routes:', err);
        setError(err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <QuantumPageLoader message="Loading application modules..." />;
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-red-600 text-xl font-bold">Failed to load routes</h1>
        <p className="text-gray-600">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
        >
          Reload
        </button>
      </div>
    );
  }

  return (
    <Routes>
      {allRoutes.map((route) => (
        <Route
          key={route.id}
          path={route.path}
          index={route.index}
          element={route.element}
        >
          {route.children?.map((child) => (
            <Route
              key={child.id}
              path={child.path}
              index={child.index}
              element={child.element}
            />
          ))}
        </Route>
      ))}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Main Router export – decides which router to use based on feature flag.
 */
export function AppRouter() {
  if (!USE_MODULAR_ROUTES) {
    console.warn('⚠️ VisaGuide OS - Legacy Router Active (VITE_USE_MODULAR_ROUTES is not set to true for this build)');
    return <LegacyRouter />;
  }
  console.log('🚀 VisaGuide OS - Modular Routing Edition');
  return <ModularRouter />;
}